import React from 'react';
import Link from 'next/link';
import posthog from 'posthog-js';
import Image from 'next/image';
import styled from 'styled-components';
import { Caption, S1Styles, P1Styles, S3, P2 } from '../../../Typography';
import Container from '../../../Container';
import MiniCarousel from '../../../MiniCarousel';
import { isUserLoggedIn } from '../../../../utils/analytics';
import { useStore } from '../../../../store';

const MastheadWrapper = styled.div`
  height: 523px;
  display: flex;
  justify-content: center;
  background-image: url('/assets/gt/gt_masthead_background.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    height: unset;
  }
`;

const MastheadContainer = styled(Container)`
  z-index: 2;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  margin: 0 16px;
  gap: 14px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 60px;
    padding: 10px 0;
  }
`;

const InfoWrapper = styled.div`
  background: linear-gradient(180deg, #673f97 0%, #452c74 100%);
  display: flex;
  padding: 18px 16px;
  border-radius: 10px;
  flex-direction: column;
  align-items: center;
  max-width: 328px;
  max-height: 158px;
  margin-bottom: 20px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 30px 16px;
    justify-content: center;
    max-width: 388px;
    max-height: 206px;
    flex-shrink: 0;
  }
`;

const InfoHeaderText = styled(S3)`
  color: ${({ theme }) => theme.colors.neutral1};
  text-align: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    ${S1Styles}
    color: ${({ theme }) => theme.colors.neutral1};
  }
`;

const InfoText = styled(Caption)`
  color: ${({ theme }) => theme.colors.neutral1};
  text-align: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    ${P1Styles}
    color: ${({ theme }) => theme.colors.neutral1};
  }
`;

const DetailsTextLink = styled.a`
  color: ${({ theme }) => theme.colors.neutral1};
  text-align: center;
`;

const DetailsText = styled(P2)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral1};
  text-align: center;
  &:hover {
    text-decoration: underline;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    font-size: 16px;
  }
`;

const InfoContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    gap: 20px;
`;

const CarouselWrapper = styled.div`
  width: 282px;
  display: flex;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: 388px;
  }
`;

const StyledImage = styled(Image)`
  border-radius: 0px 12px 12px 12px;
`;

const MobileImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: none;
  }
`;

const DesktopImageWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: flex;
    justify-content: center;
  }
`;

const GTShopMasthead: React.FC = () => {
  const { state } = useStore();

  const gtCards = [
    {
      card: 'Win 1 in 50 Ultimate Doug the Bug Prize Packs',
      image: '/assets/gt/gt_masthead_giveaway.png',
    },
    {
      card: 'Holographic Sticker Reward',
      image: '/assets/gt/gt_stretch_goal_reward_1.png',
    },
    {
      card: 'In-Game Doug the Bug Toy Reward',
      image: '/assets/gt/gt_stretch_goal_reward_2.png',
    },
    {
      card: 'Doug the Bug Holographic Trading Card Reward',
      image: '/assets/gt/gt_stretch_goal_reward_3.png',
    },
  ];
  return (
    <MastheadWrapper data-testid="gt-masthead">
      <MastheadContainer>
        <InfoWrapper>
          <InfoContentWrapper>
            <InfoHeaderText>✨ ALL NEW REWARDS! ✨</InfoHeaderText>
            <InfoText>
              Every Doug the Bug purchase helps unlock new rewards <strong>AND</strong> enters you to{' '}
              <strong>win 1 of 50 Ultimate Doug Prize Packs</strong>! 🎉
            </InfoText>
            <Link
              href="/products/doug-plushie"
              passHref
              onClick={() =>
                posthog.capture('gt_masthead_giveaway_click', {
                  is_logged_in: isUserLoggedIn(state.user),
                })
              }
            >
              <DetailsTextLink>
                <DetailsText>Click here for more details</DetailsText>
              </DetailsTextLink>
            </Link>
          </InfoContentWrapper>
        </InfoWrapper>
        <CarouselWrapper>
          <MiniCarousel autoPlayInterval={4000} showDots={gtCards.length > 1}>
            {gtCards.map((gtCard) => (
              <>
                <MobileImageWrapper>
                  <StyledImage
                    key={gtCard.card}
                    src={gtCard.image}
                    alt={gtCard.card}
                    width={282}
                    height={282}
                    priority
                    objectFit="contain"
                    data-testid="gt-masthead-image"
                  />
                </MobileImageWrapper>
                <DesktopImageWrapper>
                  <StyledImage
                    key={gtCard.card}
                    src={gtCard.image}
                    alt={gtCard.card}
                    width={376}
                    height={376}
                    priority
                    objectFit="contain"
                    data-testid="gt-masthead-image"
                  />
                </DesktopImageWrapper>
              </>
            ))}
          </MiniCarousel>
        </CarouselWrapper>
      </MastheadContainer>
    </MastheadWrapper>
  );
};
export default GTShopMasthead;
